import { AppInfo } from '../../../../constants';
import { AUTH_LOCAL_STORAGE_KEY } from './constants';
import { getQueryStringOfUrl } from '../../../../util/helpers';

export const SETUP_MARKERS_STORAGE_KEY = `${AppInfo.companyName}-setup-markers`;
export const PENDING_CALLBACK_STORAGE_KEY = `${AppInfo.companyName}-pending-callback`;

// Written before a recovery-mode completion request. It stays until the result
// is confirmed or rejected by validation, so an unclear result blocks a second
// submission for that user.
export interface SetupMarker {
  username: string;
  accountType: string;
  submittedAt: string;
}

const readJson = (key: string): unknown => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Markers are stored per username, so a submission by one user cannot
// overwrite another user's unresolved marker.
const readMarkers = (): Record<string, SetupMarker> => {
  const value = readJson(SETUP_MARKERS_STORAGE_KEY);
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, SetupMarker>) : {};
};

export const getSetupMarker = (username: string): SetupMarker | null => {
  const markers = readMarkers();
  return Object.prototype.hasOwnProperty.call(markers, username) ? markers[username] : null;
};

export const writeSetupMarker = (marker: SetupMarker) => {
  const markers = readMarkers();
  markers[marker.username] = marker;
  localStorage.setItem(SETUP_MARKERS_STORAGE_KEY, JSON.stringify(markers));
};

export const clearSetupMarker = (username: string) => {
  const markers = readMarkers();
  delete markers[username];
  if (Object.keys(markers).length === 0) {
    localStorage.removeItem(SETUP_MARKERS_STORAGE_KEY);
  } else {
    localStorage.setItem(SETUP_MARKERS_STORAGE_KEY, JSON.stringify(markers));
  }
};

// Usernames with a recovery-mode completion request in progress in this page.
// Their stored marker is not an unresolved result yet, so the app must not
// replace the form with the unresolved-setup screen. A reload starts with an
// empty set, so a stored marker then shows the screen.
const submissionsInProgress = new Set<string>();

export const startSetupSubmission = (username: string) => {
  submissionsInProgress.add(username);
};

export const endSetupSubmission = (username: string) => {
  submissionsInProgress.delete(username);
};

export const isSetupSubmissionInProgress = (username: string) => submissionsInProgress.has(username);

// A callback that could not be followed yet, because the user has no active
// role. Sign-in and an arrival with a callback save it. It is bound to the user,
// so it cannot redirect a later session.
export const savePendingCallback = (username: string, url: string) => {
  localStorage.setItem(PENDING_CALLBACK_STORAGE_KEY, JSON.stringify({ username, url: encodeURIComponent(url) }));
};

// Returns the stored callback only to its own user. The stored value is removed
// in every case.
export const takePendingCallback = (username: string): string | null => {
  const value = readJson(PENDING_CALLBACK_STORAGE_KEY) as { username?: unknown; url?: unknown } | null;
  localStorage.removeItem(PENDING_CALLBACK_STORAGE_KEY);
  if (!value || value.username !== username || typeof value.url !== 'string') {
    return null;
  }
  try {
    return decodeURIComponent(value.url);
  } catch {
    return null;
  }
};

// The callback-url value of the current URL, or null. A query string that
// cannot be decoded, for example with a malformed percent escape, counts as no
// callback, so the caller can still navigate safely.
export const readUrlCallback = (): string | null => {
  try {
    const value = getQueryStringOfUrl(true)['callback-url'];
    return typeof value === 'string' && value !== '' ? value : null;
  } catch {
    return null;
  }
};

export const isHttpUrl = (url: string) => {
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

// Called after a role becomes active. Follows the callback stored for the user,
// unless the session changed or the user has an unresolved setup. A marker
// keeps the callback stored.
export const resumePendingCallback = (username: string, sessionToken: string | null): boolean => {
  if (localStorage.getItem(AUTH_LOCAL_STORAGE_KEY) !== sessionToken || getSetupMarker(username)) {
    return false;
  }
  const url = takePendingCallback(username);
  if (!url || !isHttpUrl(url)) {
    return false;
  }
  window.location.href = url;
  return true;
};
